import React, { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Col, Row } from "@amzn/stencil-react-components/layout";
import { Text } from "@amzn/stencil-react-components/text";
import { Card } from "@amzn/stencil-react-components/card";
import { Button, ButtonVariant } from "@amzn/stencil-react-components/button";
import {
  IconCheckCircleFill,
  IconSize,
} from "@amzn/stencil-react-components/icons";
import { Link } from "@amzn/stencil-react-components/link";
import { renderBGColor } from "../../task/ui-redesign";
import { StatusEnum } from "../../../hoc/with-status";
import {
  buildUrl,
  CONTACT_US,
  I9_ADVANTAGE_URL,
  USCIS_URL,
  USCIS_URL_ES,
} from "../../../config/urls";
import ProgressRing from "../../screening-results/progress-ring.svg";
import { statusCompleteCheckColor } from "../../../config/palette";
import { isSpanishLocale } from "../../../utility/locale-helper";
import useArb from "../../../hooks/use-arb";
import { getSettings, Stage } from "../../../helpers/get-settings";
import * as adobeAnalytics from "../../../utility/adobe-analytics";
import { isDragonstone } from "../../../helpers/url-params";
import { getCandidateData } from "../../../utility/candidate-data";
import { getCaasInfo } from "../../../service/get-caas-info";
import { I9Response } from "../i9-controlled";

interface I9TaskCardProps {
  status: StatusEnum;
  shouldOverrideStatus?: Record<string, StatusEnum>;
}

export const I9TaskCardButton = ({
  status,
  shouldOverrideStatus,
}: I9TaskCardProps) => {
  const overrideStatus = shouldOverrideStatus
    ? shouldOverrideStatus[status]
    : status;
  const bundle = useArb("HVHChecklistPantherResource");
  const translatedI9Button = bundle.getMessage(
    "Checklist-MainPage-I9Legacy-ButtonText"
  );
  const { applicationId, requisitionId } = useParams<{
    applicationId: string;
    requisitionId: string;
  }>();

  const [i9Response, setI9Response] = useState<I9Response>({});

  const i9LinkAccessor = useCallback(async () => {
    const { candidateId } = await getCandidateData(applicationId);
    return getCaasInfo(candidateId);
  }, [applicationId]);

  const trackI9LinkClick = useCallback(() => {
    adobeAnalytics.addPageLoadMetric(window, applicationId, "i9advantage", {
      jobId: requisitionId,
      isDragonstone: isDragonstone(requisitionId),
    });
  }, [applicationId, requisitionId]);

  const requestI9Link = useCallback(async () => {
    try {
      const response = await i9LinkAccessor();
      setI9Response(response);
    } catch (e) {
      console.log(e);
    }
  }, [i9LinkAccessor]);

  useEffect(() => {
    requestI9Link();
  }, []);

  const redirect = useCallback(() => {
    if (!i9Response?.secureURL) {
      trackI9LinkClick();
      window.location.assign(I9_ADVANTAGE_URL);
      return;
    }
    trackI9LinkClick();

    window.location.assign(i9Response?.secureURL);
  }, [i9Response?.secureURL, trackI9LinkClick]);

  return (
    <Button
      variant={ButtonVariant.Primary}
      onClick={redirect}
      disabled={overrideStatus === StatusEnum.COMPLETED}
    >
      {translatedI9Button}
    </Button>
  );
};

export const I9TaskCard = ({
  status,
  shouldOverrideStatus,
}: I9TaskCardProps) => {
  const [stage, setStage] = useState<Stage>();

  useEffect(() => {
    getSettings().then((settings) => setStage(settings.stage));
  }, []);

  const bundle = useArb("HVHChecklistPantherResource");
  const here = bundle.getMessage("Checklist-MainPage-Link");

  const uscisLink = isSpanishLocale() ? USCIS_URL_ES : USCIS_URL;
  const supportLink = (
    <Link
      target="_blank"
      href={buildUrl(CONTACT_US, {
        stage,
      })}
    >
      {here}
    </Link>
  );
  const i9Link = (
    <Link target="_blank" href={uscisLink}>
      {here}
    </Link>
  );

  const section1CompleteMessage = bundle.formatMessage(
    "Checklist-MainPage-I9-CompletedSection1Message",
    {
      i9Link: i9Link,
      supportLink: supportLink,
    }
  );

  const pendingSection3Message = bundle.formatMessage(
    "Checklist-MainPage-I9-AlreadyCompletedMessage",
    {
      i9Link: i9Link,
      supportLink: supportLink,
    }
  );
  const translatedSectionOne = bundle.getMessage(
    "Checklist-MainPage-I9-Section1"
  );
  const translatedSectionTwo = bundle.getMessage(
    "Checklist-MainPage-I9-Section2"
  );
  const translatedCompleted = bundle.getMessage(
    "checklist-General-TaskStatus-Completed"
  );
  const translatedCompletedAtAppointment = bundle.getMessage(
    "Checklist-MainPage-I9-CompleteAtAppointment"
  );
  const translatedActionRequiredBeforeAppointment = bundle.getMessage(
    "Checklist-MainPage-I9-ActionRequiredBeforeAppointment"
  );
  const translatedI9Reverification = bundle.getMessage(
    "Checklist-MainPage-I9-Reverification"
  );
  const translatedI9Form = bundle.getMessage("Checklist-MainPage-I9-Form");
  const translatedStatus = bundle.getMessage("checklist-General-Status");
  const translatedDetails = bundle.getMessage(
    "Checklist-CardControlled-DetailsTitle"
  );

  const completionCheck = (
    <IconCheckCircleFill
      size={IconSize.Medium}
      color={statusCompleteCheckColor}
    />
  );

  const progressRing = (
    <img
      width={"24px"}
      height={"24px"}
      src={ProgressRing}
      alt="progress-ring"
    />
  );

  let sectionsToDo;
  let instructions;
  const statusAsString = status.toString();
  switch (statusAsString) {
    case "Section1Complete":
      sectionsToDo = (
        <>
          <Row alignItems={"center"} gridGap={"0.5rem"}>
            {completionCheck}
            <Text>
              {translatedSectionOne} &#183; {translatedCompleted}
            </Text>
          </Row>
          <Row alignItems={"start"} gridGap={"0.5rem"}>
            {progressRing}
            <Text>
              {translatedSectionTwo} &#183; {translatedCompletedAtAppointment}
            </Text>
          </Row>
        </>
      );
      instructions = <Text>{section1CompleteMessage}</Text>;
      break;
    case "Section2Complete":
      sectionsToDo = (
        <>
          <Row alignItems={"center"} gridGap={"0.5rem"}>
            {completionCheck}
            <Text>
              {translatedSectionOne} &#183; {translatedCompleted}
            </Text>
          </Row>
          <Row alignItems={"start"} gridGap={"0.5rem"}>
            {completionCheck}
            <Text>
              {translatedSectionTwo} &#183; {translatedCompleted}
            </Text>
          </Row>
        </>
      );
      break;
    case "PendingSection3":
      sectionsToDo = (
        <Row alignItems={"center"} gridGap={"0.5rem"}>
          {progressRing}
          <Text>
            {translatedI9Reverification} &#183;{" "}
            {translatedCompletedAtAppointment}
          </Text>
        </Row>
      );
      instructions = <Text>{pendingSection3Message}</Text>;
      break;
    case "Section3Complete":
      sectionsToDo = (
        <Row alignItems={"center"} gridGap={"0.5rem"}>
          {completionCheck}
          <Text>
            {translatedI9Reverification} &#183; {translatedCompleted}
          </Text>
        </Row>
      );
      break;
    default:
      sectionsToDo = (
        <>
          <Text>
            {translatedSectionOne} &#183;{" "}
            {translatedActionRequiredBeforeAppointment}
          </Text>
          <Text>
            {translatedSectionTwo} &#183; {translatedCompletedAtAppointment}
          </Text>
        </>
      );
      instructions = <Text>{translatedI9Form}</Text>;
      break;
  }

  return (
    <Col padding={"1rem"} gridGap={"1rem"}>
      <Card padding={"0rem"}>
        <Row>
          <Col
            backgroundColor={renderBGColor(status, shouldOverrideStatus)}
            width={"6px"}
          />
          <Col gridGap={"1rem"} padding={"1rem"}>
            <Text fontSize={"T400"}>{translatedStatus}</Text>
            {sectionsToDo}
          </Col>
        </Row>
      </Card>
      {instructions && (
        <Card padding={"0rem"}>
          <Col gridGap={"1rem"} padding={"1rem"}>
            <Text fontSize={"T400"}>{translatedDetails}</Text>
            {instructions}
          </Col>
        </Card>
      )}
    </Col>
  );
};
